import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';

const OrderTimeline = ({ orderId, user }) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newNote, setNewNote] = useState('');
    const [sending, setSending] = useState(false);
    const [showSystemLogs, setShowSystemLogs] = useState(false);
    const bottomRef = useRef(null);

    const filteredNotes = showSystemLogs ? notes : notes.filter(n => n.type !== 'system' && !n.content.includes('Sync'));

    useEffect(() => {
        fetchTimeline();
        const interval = setInterval(fetchTimeline, 30000);
        return () => clearInterval(interval);
    }, [orderId]);

    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [filteredNotes]);

    const fetchTimeline = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/orders/${orderId}/timeline`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success && response.data.data && Array.isArray(response.data.data.events)) {
                setNotes(response.data.data.events);
            } else {
                setNotes([]);
            }
        } catch (error) {
            console.error('Error fetching timeline:', error);
            setNotes([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        if (!newNote.trim()) return;
        setSending(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/orders/${orderId}/timeline`, {
                content: newNote,
                is_urgent: false
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success && response.data.data) {
                setNotes((prevNotes) => [...(Array.isArray(prevNotes) ? prevNotes : []), response.data.data]);
                setNewNote('');
            }
        } catch (error) {
            console.error('Error posting note:', error);
            toast.error('Error al guardar la nota');
        } finally {
            setSending(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const isToday = date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();
        if (isToday) {
            return 'HOY ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (loading && notes.length === 0) {
        return <div className="p-4 text-center text-gray-500 text-sm italic">Cargando...</div>;
    }

    return (
        <div className="bg-white rounded-xl flex flex-col h-full max-h-[400px]">
            <div className="flex justify-between items-center px-4 py-2 bg-gray-50/50 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <Icons.MessageSquare className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wider">Notas Operativas</span>
                </div>
                <button
                    onClick={() => setShowSystemLogs(!showSystemLogs)}
                    className={`text-[9px] font-black uppercase px-2 py-0.5 rounded transition-colors border ${showSystemLogs
                        ? 'bg-blue-600 text-white border-blue-700'
                        : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
                        }`}
                >
                    {showSystemLogs ? 'Ocultar Sistema' : 'Ver Todo'}
                </button>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-gray-50 bg-white min-h-[150px]">
                {filteredNotes.length === 0 ? (
                    <div className="text-center py-8">
                        <Icons.Inbox className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                        <p className="text-[11px] text-gray-400 font-medium italic">
                            {showSystemLogs ? 'No hay eventos registrados.' : 'Sin notas manuales relevantes.'}
                        </p>
                    </div>
                ) : (
                    filteredNotes.map((note) => (
                        <div key={note.id} className={`flex items-start gap-2 px-3 py-2 hover:bg-gray-50 transition-colors ${note.is_urgent ? 'bg-red-50' : ''}`}>
                            <div className="flex-shrink-0">
                                {note.type === 'system' ? (
                                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                                        <Icons.Settings className="w-3 h-3 text-gray-500" />
                                    </div>
                                ) : (
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white
                    ${note.user_role === 'facturador' ? 'bg-purple-500' :
                                            note.user_role === 'logistica' ? 'bg-orange-500' :
                                                note.user_role === 'mensajero' ? 'bg-green-500' :
                                                    'bg-blue-500'}`}>
                                        {(note.user_name || 'U').substring(0, 1).toUpperCase()}
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`font-semibold text-[11px] ${note.is_urgent ? 'text-red-700' : 'text-gray-900'}`}>
                                        {note.user_name}
                                    </span>
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        {formatDate(note.created_at)}
                                    </span>
                                </div>
                                <p className={`text-xs mt-0.5 leading-snug ${note.type === 'system' ? 'italic text-gray-500 text-[11px]' : 'text-gray-700'}`}>
                                    {note.content}
                                </p>

                                {(note.photo_path || note.payment_evidence_path) && (
                                    <div className="mt-2 max-w-[100px]">
                                        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                            <img
                                                src={`${process.env.REACT_APP_API_URL || 'https://apptoppingfrozen.com/api'}${(note.photo_path || note.payment_evidence_path).startsWith('/') ? '' : '/'}${note.photo_path || note.payment_evidence_path}`}
                                                alt="Evidencia"
                                                className="w-full h-full object-cover cursor-pointer hover:opacity-90"
                                                onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'https://apptoppingfrozen.com/api'}${(note.photo_path || note.payment_evidence_path).startsWith('/') ? '' : '/'}${note.photo_path || note.payment_evidence_path}`, '_blank')}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
                <div ref={bottomRef} />
            </div>

            <div className="p-3 bg-white border-t border-gray-100 rounded-b-xl">
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all"
                        placeholder="Nota rápida..."
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSend();
                            }
                        }}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!newNote.trim() || sending}
                        className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
                    >
                        {sending ? <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icons.Send className="w-3.5 h-3.5" />}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderTimeline;
